import validator from 'validator';
import { SbkCheckbox } from "@/components/checkbox/checkbox";
import { SbkCheckboxGroup } from "@/components/checkbox-group/checkbox-group";
import { SbkRadio } from "@/components/radio/radio";
import { SbkRadioGroup } from "@/components/radio-group/radio-group";
import { SbkInput } from "@/components/input/input";
import * as helpers from "@/components/validators/validators.helper";
import {
    IBAN_LENGTHS,
    REGEX_DATE_FORMAT,
    REGEX_IBAN,
    REGEX_IBAN_CODE,
    REGEX_INSURANCE_NUMBER,
    REGEX_MONTH_YEAR,
    REGEX_NAME,
    REGEX_NUMERIC,
    REGEX_OGS_EMAIL,
    REGEX_OGS_INSURED_NUMBER,
    REGEX_OGS_PASSWORD,
    REGEX_OGS_ZIP_CODE,
    REGEX_PENSION_INSURANCE_NUMBER,
    REGEX_PHONE_AREA_CODE,
    REGEX_PHONE_COUNTRY_CODE,
    REGEX_PHONE_NUMBER,
    REGEX_TELEPHONE_NUMBER,
    REGEX_URL
} from "@/assets/constants/sbkConstants.ts";
import { SbkDatepicker } from "@/components/datepicker/datepicker.ts";
import { SbkFileUploadComponent } from "@/components/file-upload/fileupload";

// eslint-disable-next-line
declare const TYPO3: any;

// eslint-disable-next-line
declare const window: any;

export type Validator = {
    args?: {
        [key: string]: string | number | boolean
    };
    // eslint-disable-next-line
    isValid: (value: string, ...rest: any[]) => boolean
    getErrorMessage: () => string
}

interface GlobalThis {
    isNationalHoliday: boolean;
    isFutureDate: boolean;
    isValidBirthdayPast: boolean;
    isValidBirthdayFuture: boolean;
    isPhoneAreaCodeTooLong: boolean;
    DatePlausibilityValidator: {
        comparisonDelta: number,
        comparisonOperator: string | null
    };
    BirthdayWstUri: string;
    isBirthday15: boolean;
}

declare let globalThis: GlobalThis;

declare global {
    const isValidBirthdayPast: boolean;
    const isValidBirthdayFuture: boolean;
}

const getMessage = (key: string): string => {
    return typeof TYPO3 !== "undefined" ? TYPO3.lang[key] : 'invalid';
}

export const RequiredValidator: Validator = {
    isValid: (value: string): boolean => {
        return !validator.isEmpty(value.trim())
    },
    getErrorMessage: () => getMessage('JsValidationCheckRequired'),
}

export const CheckedValidator: Validator = {
    isValid: (_, element): boolean => {
        if (typeof element === "undefined") {
            return true;
        }

        let allCheckboxes: SbkCheckbox[] = [];
        if (element.tagName === 'SBK-CHECKBOX-GROUP') {
            allCheckboxes = Array.from(
                element.querySelectorAll('sbk-checkbox')
            );
        } else {
            allCheckboxes.push(element);
        }

        const isSingleCheckbox = allCheckboxes.length === 1;

        CheckedValidator.args = { isSingleCheckbox: isSingleCheckbox };

        if (isSingleCheckbox) {
            return allCheckboxes[0].checked;
        }

        const checkedElements = allCheckboxes.filter((checkbox: SbkCheckbox) => checkbox.checked);
        return checkedElements.length > 0;
    },
    getErrorMessage: () => {
        return CheckedValidator.args?.isSingleCheckbox
            ? getMessage('JsValidationCheckRequired')
            : getMessage('JsValidationCheckRequiredOption');
    },
};

export const EmailValidator: Validator = {
    isValid: (value: string): boolean => {
        return !validator.isEmpty(value.trim()) ? validator.isEmail(value) : true
    },
    getErrorMessage: () => getMessage('JsValidationCheckEmail'),
}

export const YearValidator: Validator = {
    isValid: (value: string): boolean => {
        return !validator.isEmpty(value.trim())
            ? validator.isNumeric(value, { no_symbols: true }) && validator.isLength(value, { min: 4, max: 4 })
            : true
    },
    getErrorMessage: () => getMessage('JsValidationCheckForYear'),
}

export const MonthValidator: Validator = {
    isValid: (value: string): boolean => {
        return !validator.isEmpty(value.trim())
            ? validator.isNumeric(value, { no_symbols: true }) &&
            validator.isLength(value, { min: 2, max: 2 }) &&
            validator.isInt(value, { min: 1, max: 12 })
            : true
    },
    getErrorMessage: () => getMessage('JsValidationCheckForMonth'),
}

export const DayValidator: Validator = {
    isValid: (value: string, month: number, year: number): boolean => {
        const lastDayOfMonthInYear = new Date(year, month, 0).getDate()

        return !validator.isEmpty(value.trim())
            ? validator.isNumeric(value, { no_symbols: true }) &&
            validator.isLength(value, { min: 2, max: 2 }) &&
            validator.isInt(value, { min: 1, max: lastDayOfMonthInYear })
            : true
    },
    getErrorMessage: () => getMessage('JsValidationCheckForDay'),
}

export const DateValidator: Validator = {
    isValid: (value: string): boolean => {
        // Already caught by Required Validator
        if (validator.isEmpty(value.trim())) {
            return true;
        }

        if (!helpers.checkDateFormat(value)) return false

        const isAltFormat = validator.contains(value, '.');

        const formattedDate = isAltFormat ? value.split('.').reverse().join('-') : value;

        const splittedDate = formattedDate.split('-');

        // Check if all values are set
        if (!splittedDate[0] || !splittedDate[1] || !splittedDate[2]) {
            return false;
            // Check if all values are numbers
        } else if (
            !validator.isNumeric(splittedDate[0]) ||
            !validator.isNumeric(splittedDate[1]) ||
            !validator.isNumeric(splittedDate[2])
        ) {
            return false;
        }

        const year = splittedDate[0];
        const isValidYear = YearValidator.isValid(year);

        const month = splittedDate[1];
        const isValidMonth = MonthValidator.isValid(month);

        const day = splittedDate[2];
        const isValidDay = DayValidator.isValid(day, parseInt(month), parseInt(year));

        const date = validator.toDate(formattedDate);
        const isValidDate = date !== null;
        DateValidator.args = {
            isPlausible: isValidDate && isValidYear && isValidMonth && isValidDay,
        };
        return !!DateValidator.args.isPlausible;
    },
    getErrorMessage: () => {
        if (DateValidator.args && !DateValidator.args.isPlausible) {
            return getMessage('JsValidationNotValidDate')
        }
        return getMessage('JsValidationCheckDate')
    },
}


export const BirthdayPastValidator: Validator = {
    isValid: (value: string): boolean => {
        const currentYear = new Date().getFullYear()

        const isAltFormat = validator.contains(value, '.')

        const formattedDate = isAltFormat ? value.split('.').reverse().join('-') : value
        const splittedDate = formattedDate.split('-')

        // Validate date
        const year = parseInt(splittedDate[0])

        return ((currentYear - year) <= 120)
    },
    getErrorMessage: () => getMessage('JsValidationCheckBirthdayPast'),
}

export const BirthdayFutureValidator: Validator = {
    isValid: (value: string): boolean => {
        const isAltFormat = validator.contains(value, '.')

        const formattedDate = isAltFormat ? value.split('.').reverse().join('-') : value
        const splittedDate = formattedDate.split('-')

        // Validate date
        const day = parseInt(splittedDate[2])
        const month = parseInt(splittedDate[1])
        const year = parseInt(splittedDate[0])

        const currentTimestamp = new Date(new Date().setHours(0, 0, 0, 0));
        const birthdayTimestamp = new Date(year, (month - 1), day, 0, 0, 0)

        return (currentTimestamp.getTime() >= birthdayTimestamp.getTime())
    },
    getErrorMessage: () => getMessage('JsValidationCheckBirthdayFuture')
}

export const BirthdayValidator: Validator = {
    isValid: (value: string): boolean => {
        // Already catched by Required Validator and Date Validator
        if (validator.isEmpty(value.trim()) || !DateValidator.isValid(value)) {
            return true
        }
        const isAltFormat = validator.contains(value, '.')
        const formattedDate = isAltFormat ? value.split('.').reverse().join('-') : value

        const isValidBirthdayPast = BirthdayPastValidator.isValid(formattedDate)
        const isValidBirthdayFuture = BirthdayFutureValidator.isValid(formattedDate)
        BirthdayValidator.args = {
            isValidBirthdayPast: isValidBirthdayPast,
            isValidBirthdayFuture: isValidBirthdayFuture
        };

        return isValidBirthdayPast && isValidBirthdayFuture
    },
    getErrorMessage: () => {
        if (BirthdayValidator.args && !BirthdayValidator.args.isValidBirthdayPast) {
            return getMessage('JsValidationCheckBirthdayPast')
        }
        if (BirthdayValidator.args && !BirthdayValidator.args.isValidBirthdayFuture) {
            return getMessage('JsValidationCheckBirthdayFuture')
        }
        return getMessage('JsValidationCheckBirthday')
    },
}


export const BirthdayMaxAgeValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        // Already catched by Required Validator and Date Validator
        if (validator.isEmpty(value.trim()) || !DateValidator.isValid(value)) {
            return true
        }
        const isAltFormat = validator.contains(value, '.')
        const formattedDate = isAltFormat ? value.split('.').reverse().join('-') : value
        const datepicker = <HTMLElement>element.closest('sbk-datepicker')

        const regExpMatchArray = datepicker.className.match(/\d+/)
        if (regExpMatchArray === null) {
            return true
        }
        const maxAge = parseInt(regExpMatchArray[0])

        const date = new Date()
        date.setFullYear(date.getFullYear() - maxAge)
        return validator.isAfter(formattedDate, date.toString())
    },
    getErrorMessage: () => getMessage('JsValidationCheckBirthdayYounger25Years'),
}

export const NumberValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        if (isNaN(parseInt(value)) || parseInt(value) < 0) {
            return false
        }

        const x = parseFloat(value)

        return ((x | 0) === x)
    },
    getErrorMessage: () => getMessage('JsValidationCheckNumber'),
}

export const NumericValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        return !!value.match(REGEX_NUMERIC)
    },
    getErrorMessage: () => getMessage('validator.numeric.notvalid'),
}

export const CurrencyValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        return validator.isCurrency(value, {
            decimal_separator: ',',
            thousands_separator: '.',
            allow_negatives: false,
        })
    },
    getErrorMessage: () => getMessage('JsValidationCheckCurrency'),
}

export const LowerThanTimeValidator: Validator = {
    isValid: (value: string, element: HTMLSelectElement): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        const nextSelectField = element.nextElementSibling?.querySelector('sbk-select')

        const currValue = parseInt(value)
        const nextValue = parseInt(nextSelectField?.selectedOption.value ?? '')

        return (nextValue > currValue)
    },
    getErrorMessage: () => getMessage('JsValidationCheckLowerThanTime'),
}

export const GreaterThanTimeValidator: Validator = {
    isValid: (value: string, element: HTMLSelectElement): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        const previousSelectField = element.previousElementSibling?.querySelector('sbk-select');
        const currValue = parseInt(value)
        const previousValue = parseInt(previousSelectField?.selectedOption.value ?? '')

        // Add a further validation to the previous select to avoid higher "froms"
        previousSelectField?.className.replace('validate[', 'validate[funcCall[mms.Validator.validateLowerThanTime],')
        return (currValue > previousValue)
    },
    getErrorMessage: () => getMessage('JsValidationCheckGreaterThanTime'),
}

export const WorkdayValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        // First do date validation
        const dateValidation = DateValidator.isValid(value)
        if (!dateValidation) {
            return false
        }

        const formatDate = helpers.getCurrentDate()
        // Get date of input
        const selectedDate = helpers.getFormattedSelectedDate(value)

        // Do futureDay validation
        globalThis.isFutureDate = FutureDateValidator.isValid(value)
        if (!globalThis.isFutureDate) {
            return false
        }

        // On the same day it must be earlier than 5pm
        const today = new Date()
        const hour = today.getHours()

        if (!selectedDate) return false
        if (selectedDate.getTime() === formatDate.getTime()) {
            if (hour >= 17) {
                return false
            }
        }

        // Not input of saturday and sunday allowed
        return (!(selectedDate.getDay() === 6 || selectedDate.getDay() === 0))
    },
    getErrorMessage: () => {
        if (!globalThis.isFutureDate) {
            return FutureDateValidator.getErrorMessage()
        }

        return getMessage('JsValidationCheckWeekday');
    }
}

export const CallBackDayValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        // First do date validation
        const dateValidation = DateValidator.isValid(value)
        if (!dateValidation) {
            return false
        }

        // Do workDay validation
        if (!WorkdayValidator.isValid(value)) {
            return false
        }

        // Do holiday validation
        globalThis.isNationalHoliday = !HolidayValidator.isValid(value, element)
        if (globalThis.isNationalHoliday) {
            return false
        }

        const formatDate = helpers.getCurrentDate()

        // Get date of input
        const selectedDate = helpers.getFormattedSelectedDate(value)

        // Check if day is a maximum of two months in the future (5270400000 ms === 2 months)
        return !!selectedDate && (selectedDate.getTime() <= formatDate.getTime() + 5270400000)
    },
    getErrorMessage: () => {
        if (globalThis.isNationalHoliday) {
            return HolidayValidator.getErrorMessage()
        }

        // the error message also matches workingDay- and futureDate-validation
        return getMessage('JsValidationCheckCallBackDay');
    }
}

export const IbanValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        const iban = value.trim().toUpperCase().replace(/\s+/g, '') // format iban to uppercase & delete all spaces
        const code = iban.match(REGEX_IBAN_CODE) // match and capture (1) the country code, (2) the check digits, and (3) the rest

        if (!code) return false

        const countryCode = code[1]
        const checkDigits = code[2]
        const restOfIban = code[3]

        if (iban.length !== IBAN_LENGTHS[countryCode]) {
            return false
        }

        if (!iban.match(REGEX_IBAN[countryCode])) {
            return false
        }

        // checksum validation
        // first rearrange digits
        const digits = `${restOfIban}${countryCode}${checkDigits}`.replace(/[A-Z]/g, (letter: string): string => {
            return (letter.charCodeAt(0) - 55).toString()
        })

        const checksum = helpers.getChecksum(digits)

        return (checksum === 1)
    },
    getErrorMessage: () => {
        return getMessage('iban.error.validation')
    }
}

export const KvnrValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        const kvnr = value.trim().toUpperCase()
        return REGEX_INSURANCE_NUMBER.test(kvnr)
    },
    getErrorMessage: () => {
        return getMessage('kvnr.error.validation')
    }
}

export const PhoneCountryCodeValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        return REGEX_PHONE_COUNTRY_CODE.test(value)
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckPhoneCountryCode')
    }
}

export const PhoneAreaValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        if (!REGEX_PHONE_AREA_CODE.test(value)) {
            return false
        }

        return (value.length < 5)
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckPhoneArea')
    }
}

export const PhoneAreaCodeValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        if (!REGEX_PHONE_AREA_CODE.test(value)) {
            globalThis.isPhoneAreaCodeTooLong = false
            return false
        }

        globalThis.isPhoneAreaCodeTooLong = true
        return (value.length <= 5);
    },
    getErrorMessage: () => {
        if (globalThis.isPhoneAreaCodeTooLong) {
            return getMessage('JsValidationPhoneAreaCodeLength');
        }
        return getMessage('JsValidationCheckPhoneAreaCode');
    }
}

export const PhoneNumberValidator: Validator = {
    isValid: (value: string): boolean => {

        if (validator.isEmpty(value.trim())) {
            return true
        }

        return REGEX_PHONE_NUMBER.test(value)
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckPhoneNumber')
    }
}

export const TelephoneNumberValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        return REGEX_TELEPHONE_NUMBER.test(value)
    },
    getErrorMessage: () => {
        return getMessage('validator.telephoneNumber.notvalid');
    }
}

export const PensionInsuranceValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        if (!REGEX_PENSION_INSURANCE_NUMBER.test(value)) {
            return false
        }

        return (!(value.length !== 0 && value.length !== 12))
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckPensionInsuranceNumberVpt')
    }
}

export const BankDetailsMmsRadioValidator: Validator = {
    isValid: (_, element): boolean => {

        if (typeof element === "undefined") {
            return true;
        }

        const allElementsFromGroup: SbkRadio[] = Array.from(
            element.querySelectorAll('sbk-radio')
        );

        const checkedElements =
            allElementsFromGroup &&
            allElementsFromGroup.filter((element: SbkRadio) => {
                return element.checked && element
            });

        return checkedElements.length === 1;
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckBankdetailsmmsRadio')
    }
}

export const BonusprogramDateValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return false
        }

        if (!helpers.checkDateFormat(value)) return false

        if (!DateValidator.isValid(value)) {
            return false
        }

        const splittedDate = value.split(REGEX_DATE_FORMAT)

        // Validate date
        const day = parseInt(splittedDate[5])
        const month = parseInt(splittedDate[3])
        const year = parseInt(splittedDate[1])
        const currentDateMinus1Y = new Date()
        currentDateMinus1Y.setDate(currentDateMinus1Y.getDate() - 6);
        currentDateMinus1Y.setFullYear(currentDateMinus1Y.getFullYear() - 1);
        const currentDatePlus1Y = new Date()
        currentDatePlus1Y.setFullYear(currentDatePlus1Y.getFullYear() + 1);
        const birthdayDate = new Date(`${year} ${month} ${day}`)

        // Child must not be older than 1 year and 5 days
        if (currentDateMinus1Y.getTime() > birthdayDate.getTime()) {
            return false
        }

        // Participation is only possible from the beginning of pregnancy (plus one year)
        return (!(currentDatePlus1Y.getTime() < birthdayDate.getTime()))
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckBonusprogrammDate')
    }
}

export const NumberOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        // Is not a number or lower than 0
        if (isNaN(parseInt(value)) || parseInt(value) < 0) {
            return false
        }

        // Greater than 40
        return (parseInt(value) < 40)
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckNumberOgs');
    }
}

export const ZipOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        return REGEX_OGS_ZIP_CODE.test(value.trim())
    },
    getErrorMessage: () => {
        return getMessage('postleitzahl.error.validation');
    }
}

export const BirthdayOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        if (!DateValidator.isValid(value)) return false

        const splittedDate = value.split(REGEX_DATE_FORMAT)

        // Validate date
        const day = parseInt(splittedDate[1])
        const month = parseInt(splittedDate[3])
        const year = parseInt(splittedDate[5])

        const birthdate = new Date(year, month - 1, day)

        const lowerRange = new Date()
        const upperRange = new Date()

        lowerRange.setFullYear(lowerRange.getFullYear() - 120)
        upperRange.setFullYear(upperRange.getFullYear() - 15)

        return !(birthdate.getDate() !== day ||
            birthdate.getMonth() !== month - 1 ||
            birthdate.getFullYear() !== year ||
            birthdate < lowerRange ||
            upperRange < birthdate);
    },
    getErrorMessage: () => {
        return getMessage('geburtsdatum.error.validation');
    }
}

export const InsuredNumberOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        return REGEX_OGS_INSURED_NUMBER.test(value.trim())
    },
    getErrorMessage: () => {
        return getMessage('versichertennummer.error.validation');
    }
}

export const PasswordOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        return REGEX_OGS_PASSWORD.test(value)
    },
    getErrorMessage: () => {
        return getMessage('passwortNeu.error.validation');
    }
}

export const EmailOgsValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }
        return REGEX_OGS_EMAIL.test(value.trim().toLowerCase())
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckEmail');
    }
}

export const ForFamilyMembersMmsRadioValidator: Validator = {
    isValid: (_: string, element: HTMLInputElement): boolean => {
        const form = element.closest('form')
        const name = element.getAttribute('name')
        if (form) {
            const checkedInputs = Array.from(form.querySelectorAll(`input[name='${name}']:checked`))
            const noCheckedInputs = checkedInputs.length === 0
            return !noCheckedInputs
        }
        return false;
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckForFamilyMembersMmsRadio');
    }
}

export const TermsAndConditionsValidator: Validator = {
    isValid: (_: string, element: HTMLInputElement): boolean => {
        const checkedFields = element.closest('.fieldContent')?.querySelectorAll('input[type=checkbox]:checked').length
        return (!checkedFields) ? false : true
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckTermsAndConditions');
    }
}

export const FutureDateValidator: Validator = {
    isValid: (value: string): boolean => {
        // Already caught by Required Validator
        if (validator.isEmpty(value.trim())) {
            return true;
        }

        if (!DateValidator.isValid(value)) {
            return false;
        }

        const today = helpers.getCurrentDate()
        const givenDate = helpers.getFormattedSelectedDate(value);
        return !!givenDate && (today.getTime() <= givenDate.getTime());
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckFutureDate');
    }
}

export const PastDateValidator: Validator = {
    isValid: (value: string): boolean => {
        // Already caught by Required Validator
        if (validator.isEmpty(value.trim())) {
            return true;
        }

        if (!DateValidator.isValid(value)) {
            return false;
        }

        const today = helpers.getCurrentDate();
        const givenDate = helpers.getFormattedSelectedDate(value);

        return !!givenDate && givenDate.getTime() <= today.getTime();
    },
    getErrorMessage: () => getMessage('JsValidationCheckPastDate'),
};

export const UploadedValidator: Validator = {
    isValid: (_, element: HTMLInputElement): boolean => {
        const wrapper = element.closest('.fileupload')
        if (!wrapper) {
            return false
        }
        const filesList = wrapper.querySelector('.files-list')
        if (!filesList) {
            return false
        }
        const filesListInputs: HTMLInputElement[] = Array.from(filesList.querySelectorAll('.fileupload__file-container input'))

        if (filesListInputs.length === 0) {
            return false
        }

        filesListInputs.forEach((filesListInput: HTMLInputElement) => {
            if (validator.isEmpty(filesListInput.value)) {
                return false
            }
        })

        return true
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckRequired');
    },
}

export const MonthYearValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value.trim())) {
            return true
        }

        return REGEX_MONTH_YEAR.test(value)
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckForMonthYear');
    },
}

export const MinimumCheckboxValidator: Validator = {
    isValid: (_, element): boolean => {
        if (typeof element === "undefined") {
            return true;
        }
        const regExpMatchArray = element.className.match(/\d{1}/)
        if (regExpMatchArray === null) {
            return true
        }
        const nbCheck = parseInt(regExpMatchArray[0])
        MinimumCheckboxValidator.args = {
            minCheckboxes: nbCheck
        };

        const allElementsFromGroup: SbkCheckbox[] = Array.from(
            element.querySelectorAll('sbk-checkbox')
        );

        const checkedElements =
            allElementsFromGroup &&
            allElementsFromGroup.filter((element: SbkCheckbox) => {
                return element.checked && element
            });

        return checkedElements.length >= nbCheck;
    },
    getErrorMessage: () => {
        let minCheckboxes;
        if (typeof MinimumCheckboxValidator.args !== "undefined") {
            minCheckboxes = MinimumCheckboxValidator.args.minCheckboxes;
        }
        return typeof TYPO3 !== "undefined" ? TYPO3.lang.JsValidationMinCheckboxBegin + minCheckboxes + TYPO3.lang.JsValidationMinCheckboxEnd : 'min ' + minCheckboxes
    }
}

export const UrlValidator: Validator = {
    isValid: (value: string): boolean => {
        return !validator.isEmpty(value.trim()) ? REGEX_URL.test(value.trim()) : true
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckUrl');
    },
}

export const HolidayValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        // First do date validation
        if (validator.isEmpty(value.trim()) || !DateValidator.isValid(value)) {
            return true;
        }

        // check if selected date is in the list of holidays
        if (element) {
            const dataHolidaysAttr = element.getAttribute('data-holidays');
            if (dataHolidaysAttr) {
                const holidays = dataHolidaysAttr.split('|');
                const inputDate = helpers.getFormattedSelectedDate(value);

                if (inputDate) {
                    const unixTimeStamp = Math.floor(inputDate.getTime() / 1000);
                    return !holidays.includes(String(unixTimeStamp));
                }
            }
        }
        return true;
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckHoliday');
    }
};

export const DatePlausibilityValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        // First do date validation
        if (validator.isEmpty(value.trim()) || !DateValidator.isValid(value)) {
            return true
        }
        const comparisonFieldIdentifier = element.getAttribute('data-date-plausibility-comparison-field')
        const comparisonField = element.closest('form')?.querySelector(`[id$='${comparisonFieldIdentifier}']`)
        const comparisonOperator = element.getAttribute('data-date-plausibility-comparison-operator')
        const comparisonDelta = parseInt(element.getAttribute('data-date-plausibility-comparison-delta') ?? '')
        const comparisonValue = (<SbkDatepicker>comparisonField)?.datepickerValue

        const givenDate = helpers.getFormattedSelectedDate(value)
        const comparisonDate = helpers.getFormattedSelectedDate(comparisonValue)

        globalThis.DatePlausibilityValidator = {
            comparisonDelta: comparisonDelta,
            comparisonOperator: comparisonOperator
        }

        if (!isNaN(comparisonDelta) && comparisonDelta !== 0) {
            comparisonDate?.setDate(comparisonDate.getDate() + comparisonDelta)
        }

        if (givenDate && comparisonDate) {
            switch (comparisonOperator) {
                case 'greaterThan':
                    return givenDate.getTime() >= comparisonDate.getTime();
                case 'lessThan':
                    return givenDate.getTime() <= comparisonDate.getTime();
                case 'equals':
                    return givenDate.getTime() == comparisonDate.getTime();
            }
        }
        return true;
    },
    getErrorMessage: () => {

        const comparisonDelta = globalThis.DatePlausibilityValidator.comparisonDelta
        const comparisonDeltaAbsolute = Math.abs(comparisonDelta)
        const dayLabel = comparisonDeltaAbsolute === 1 ? TYPO3.lang["JsValidationCheckDatePlausibility.Day"] : TYPO3.lang["JsValidationCheckDatePlausibility.Days"]
        const comparisonOperator = globalThis.DatePlausibilityValidator.comparisonOperator

        let errorMessage
        if (comparisonOperator === 'equals') {
            errorMessage = TYPO3.lang["JsValidationCheckDatePlausibility.NotEqual"]
            errorMessage = errorMessage.replace('%d', comparisonDeltaAbsolute + 1)
            errorMessage = errorMessage.replace('%s', dayLabel)
        } else {
            errorMessage = TYPO3.lang["JsValidationCheckDatePlausibility"]
            if (isNaN(comparisonDeltaAbsolute) || comparisonDeltaAbsolute === 0) {
                errorMessage = errorMessage.replace('%s', comparisonOperator === 'lessThan' ? TYPO3.lang["JsValidationCheckDatePlausibility.Before"] : TYPO3.lang["JsValidationCheckDatePlausibility.After"])
            } else {
                let deltaLabel = TYPO3.lang["JsValidationCheckDatePlausibility.AtLeast"]
                if (comparisonOperator === 'lessThan') {
                    if (comparisonDelta > 0) {
                        deltaLabel = TYPO3.lang["JsValidationCheckDatePlausibility.NotMore"]
                    }
                }
                if (comparisonOperator === 'greaterThan') {
                    if (comparisonDelta < 0) {
                        deltaLabel = TYPO3.lang["JsValidationCheckDatePlausibility.NotMore"]
                    }
                }

                deltaLabel = deltaLabel.replace('%d', String(comparisonDeltaAbsolute))
                deltaLabel = deltaLabel.replace('%s', dayLabel)
                deltaLabel = deltaLabel.replace('%s', comparisonDelta < 0 ? TYPO3.lang["JsValidationCheckDatePlausibility.Before"] : TYPO3.lang["JsValidationCheckDatePlausibility.After"])
                errorMessage = errorMessage.replace('%s', deltaLabel)
            }
        }

        return errorMessage
    }
}

export const DateInvalidValidator: Validator = {
    isValid: (_: string, element: HTMLInputElement): boolean => {
        const invalidDate = element.closest('sbk-datepicker')?.getAttribute('data-invalid-date')
        return !invalidDate
    },
    getErrorMessage: () => {
        return getMessage('JsValidationNotValidDate');
    }
}

export const RadioButtonValidator: Validator = {
    isValid: (_, element): boolean => {

        if (typeof element === "undefined") {
            return true;
        }

        const allElementsFromGroup: SbkRadio[] = Array.from(
            element.querySelectorAll('sbk-radio')
        );

        const checkedElements =
            allElementsFromGroup &&
            allElementsFromGroup.filter((element: SbkRadio) => {
                return element.checked && element
            });

        return checkedElements.length === 1;
    },
    getErrorMessage: () => getMessage('JsValidationCheckRequiredOption'),
}

export const WeeklyWorkHoursValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        const currVal = value.replace(',', '.')
        const checkboxGroup = element.nextElementSibling;

        if (validator.isEmpty(value)) {
            return true
        }

        // Lower than 0
        if (+currVal < 0) {
            return false
        }

        // Greater than 48
        if (+currVal > 48) {
            return false
        }
        // depending on given number value a minimum of checkboxes must be checked
        // set new validation class for the checkboxes depending on given number value

        let validationClass = "validate[minCheckbox[1]]"
        switch (true) {
            case (+currVal > 30):
                validationClass = "validate[minCheckbox[4]]"
                break
            case (+currVal > 20):
                validationClass = "validate[minCheckbox[3]]"
                break
            case (+currVal > 10):
                validationClass = "validate[minCheckbox[2]]"
                break
            default:
                break
        }

        if (checkboxGroup) {
            const groupCurrentClasses = checkboxGroup.classList;
            for (let i = 0; i < groupCurrentClasses.length; i++) {
                if (groupCurrentClasses[i].indexOf("validate[") >= 0) {
                    checkboxGroup.classList.remove(checkboxGroup.classList[i])
                }
            }
            // now set new validation class
            checkboxGroup.classList.add(validationClass);
            (checkboxGroup as SbkCheckboxGroup)._checkValidity();
        }
        return true
    },
    getErrorMessage: () => {
        return TYPO3.lang.JsValidationCheckMaxWeeklyWorkHoursWst
    },
}

export const MinimumWageValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        let monthlyWage = '';
        let monthlyWageSelection = 0;

        if (element.getAttribute('id') === 'averageMonthlyWagesCustom') {
            monthlyWage = value.replace(/,/g, '.');
        } else {
            const radioButtons = Array.from(element.querySelectorAll('sbk-radio'))
            radioButtons.forEach((radio: Element) => {
                if ((radio as SbkRadio).checked) {
                    value = (radio as SbkRadio).value;
                }
            });

            monthlyWageSelection = parseInt(value.split("#")[0]);
            monthlyWage = value.split("#")[1];

            // Manipulate wages based on selection
            if (+monthlyWageSelection === 1) {
                monthlyWage = '325';
            } else if (+monthlyWageSelection === 2) {
                monthlyWage = value.split("#")[1];
            } else if (+monthlyWageSelection === 4) {
                monthlyWage = value.split("#")[2];
            }
        }

        // No validation possible for wages greater than Geringfügigkeitsgrenze (Selection 3)
        if (+monthlyWageSelection !== 3) {
            const fromField = document.querySelector<HTMLInputElement>(`#${element.getAttribute('data-field-from')}`) as unknown as SbkDatepicker;
            const toField = document.querySelector<HTMLInputElement>(`#${element.getAttribute('data-field-to')}`) as unknown as SbkDatepicker;
            const workHoursField = document.querySelector<HTMLInputElement>(`#${element.getAttribute('data-field-workhours')}`);
            const dateField = document.querySelector<SbkDatepicker>('#birthDate');

            if (!fromField || !toField || !workHoursField || !dateField) {
                return false; // Return false if any of the required fields are not found
            }

            const fromStrArray = fromField.datepickerValue.split(".");
            const fromStamp = Math.floor(new Date(`${fromStrArray[1]}/${fromStrArray[0]}/${fromStrArray[2]} 00:00:00`).getTime() / 1000);
            const toStrArray = toField.datepickerValue.split(".");
            const toStamp = Math.floor(new Date(`${toStrArray[1]}/${toStrArray[0]}/${toStrArray[2]} 00:00:00`).getTime() / 1000);
            let weeks = (toStamp - fromStamp) / (7 * 24 * 60 * 60) >= 4 ? 4 : (toStamp - fromStamp) / (7 * 24 * 60 * 60);

            // toField and toStamp is empty if unbefristete Beschäftigung
            if (!toStamp) {
                weeks = 4;
            }

            const weeklyWage = +monthlyWage / weeks;
            const currentWage = weeklyWage / +workHoursField.value;

            const dateString = dateField.datepickerValue.includes("-")
                ? dateField.datepickerValue.split("-")
                : dateField.datepickerValue.split(".");
            const birthdate = new Date(+dateString[0], +dateString[1] - 1, +dateString[2]);

            const today = new Date();
            const years = Math.floor((today.getTime() - birthdate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

            // Minimum wages
            const wageTimestamps = {
                first: Math.floor(new Date("2021/01/01 00:00:00").getTime() / 1000),
                second: Math.floor(new Date("2021/07/01 00:00:00").getTime() / 1000),
                third: Math.floor(new Date("2022/01/01 00:00:00").getTime() / 1000),
                fourth: Math.floor(new Date("2022/07/01 00:00:00").getTime() / 1000),
            };

            let legalMinimumWage = 9.19;
            if (fromStamp >= wageTimestamps.fourth) {
                legalMinimumWage = 10.45;
            } else if (fromStamp >= wageTimestamps.third) {
                legalMinimumWage = 9.82;
            } else if (fromStamp >= wageTimestamps.second) {
                legalMinimumWage = 9.60;
            } else if (fromStamp >= wageTimestamps.first) {
                legalMinimumWage = 9.50;
            }

            return (years > 18) && (currentWage > legalMinimumWage);
        }

        return true;
    },
    getErrorMessage: () => TYPO3.lang.JsValidationCheckMinimumWage,
}

export const YearVptValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value)) {
            return true
        }
        return /^(19|20)\d{2}$/.test(value);
    },
    getErrorMessage: () => getMessage('JsValidationCheckForYear')
}

export const RequiredResumableUploadValidator: Validator = {
    isValid: (_, element: SbkFileUploadComponent): boolean => {
        return element.files.length > 0
    },
    getErrorMessage: () => getMessage('JsValidationCheckRequired'),
}

export const BirthdayWstValidator: Validator = {
    isValid: (value: string): boolean => {
        if (validator.isEmpty(value)) {
            return true
        }

        // Validate date format
        const dateArray = value.match(/^(\d{2})(\.|-)(\d{2})(\.|-)(\d{4})$/)

        // Date format not valid
        if (dateArray === null) {
            return false
        }

        // Validate date
        const day = +dateArray[1]
        const month = +dateArray[3]
        const year = +dateArray[5]

        if (month < 1 || month > 12) {
            return false
        } else if (day < 1 || day > 31) {
            return false
        } else if ((month == 4 || month == 6 || month == 9 || month == 11) && day == 31) {
            return false
        }
        // Leap year
        else if (month == 2) {
            const isLeap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0))
            if (day > 29 || (day == 29 && !isLeap)) {
                return false
            }
        }

        // Birthday must at least 16 years
        const minAge = 15
        const myDate = new Date()

        const fullYear = new Date(myDate.setFullYear(year, month - 1, day - 1))

        const currDate = Date.now()
        const setDate = new Date()
        const minYear = setDate.setFullYear(fullYear.getFullYear() + minAge, month - 1, day - 1)

        if ((currDate - minYear) <= 0) {
            let redirectUrl = ''
            const isSelfService = window["isSelfService"]
            const isLanguage = window["isLanguage"]

            if (typeof isSelfService !== 'undefined') {
                // handling for selfservice
                if (isLanguage === 'de') {
                    redirectUrl = 'https://werkstudenten.sbk.org/selfservice-hinweis/'
                } else {
                    redirectUrl = 'https://werkstudenten.sbk.org/en/selfservice-note/'
                }
            } else {
                // handling for corporate client form
                if (isLanguage === 'de') {
                    redirectUrl = 'https://werkstudenten.sbk.org/pdf-formular/'
                } else {
                    redirectUrl = 'https://werkstudenten.sbk.org/en/pdf-formular/'
                }
            }

            BirthdayWstValidator.args = {
                BirthdayWstUri: redirectUrl,
                isBirthday15: true
            }

            return false
        }

        BirthdayWstValidator.args = {};
        return BirthdayPastValidator.isValid(value)
    },
    getErrorMessage: () => {
        if (BirthdayWstValidator.args && BirthdayWstValidator.args.isBirthday15) {
            return TYPO3.lang.JsValidationCheckBirthdayWst15 + '<sbk-link variant="regular" href="' + BirthdayWstValidator.args.BirthdayWstUri + '" title="" >' + TYPO3.lang.JsValidationCheckBirthdayWst15ToAssessmentSheet + '</sbk-link>'
        }

        return getMessage('JsValidationCheckBirthdayWst')
    },
}

export const NextCareerMoveWstValidator: Validator = {
    isValid: function () {
        const radioButtons = Array.from(document.querySelectorAll('.nextCareerMove sbk-radio')) as SbkRadio[];
        const yesSelected = radioButtons.some((radio: SbkRadio) => {
            return radio.checked && radio.value === 'JA'
        });
        const input = document.querySelector('#pupilOthers') as SbkInput;

        const result = yesSelected || input.value.length > 0;

        if (result) {
            radioButtons.forEach((radio: SbkRadio) => {
                radio.invalid = false;
                (radio.parentElement! as SbkRadioGroup).invalid = false;
            });
            input.invalid = false;
        }

        return result;
    },
    getErrorMessage: function () {
        return getMessage('JsValidationCheckNextCareerMoveWst')
    },
};

export const FromDateValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        if (validator.isEmpty(value)) {
            return true
        }
        const currArr = value.split('.')
        const currDate = new Date(+currArr[2], (+currArr[1] - 1), +currArr[0])

        const comparisonFieldIdentifier = element.closest('sbk-datepicker')?.getAttribute('data-from-date-validator-comparison-field')
        const comparisonField: SbkDatepicker | null | undefined = element.closest('form')?.querySelector(`#${comparisonFieldIdentifier}`)
        if (!comparisonField || comparisonField.hidden) {
            return true;
        }
        const comparisonValue: string | null = comparisonField.getAttribute('datepicker-value')

        if (!comparisonValue) {
            return true
        }

        const nextDate = helpers.getFormattedSelectedDate(comparisonValue)

        if (!nextDate) {
            return true
        }

        return +nextDate >= +currDate
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckFromDate')
    },
}

export const ToDateValidator: Validator = {
    isValid: (value: string, element: HTMLInputElement): boolean => {
        if (validator.isEmpty(value)) {
            return true
        }
        const currArr = value.split('.')
        const currDate = new Date(+currArr[2], (+currArr[1] - 1), +currArr[0])

        const comparisonFieldIdentifier = element.closest('sbk-datepicker')?.getAttribute('data-to-date-validator-comparison-field')
        const comparisonField: SbkDatepicker | null | undefined = element.closest('form')?.querySelector(`#${comparisonFieldIdentifier}`)

        if (!comparisonField) {
            return true;
        }
        const comparisonValue: string | null = comparisonField.getAttribute('datepicker-value')
        if (!comparisonValue) {
            return true
        }

        const nextDate = helpers.getFormattedSelectedDate(comparisonValue)

        if (!nextDate) {
            return true
        }

        return +nextDate <= +currDate
    },
    getErrorMessage: () => {
        return getMessage('JsValidationCheckToDate')
    },
}

export const DataAgreementValidator: Validator = {
    isValid: (_, element): boolean => {

        if (typeof element === "undefined") {
            return true;
        }

        const checkbox = element.querySelector('sbk-checkbox');

        return checkbox.checked;
    },
    getErrorMessage: function () {
        return TYPO3.lang.JsValidationCheckDataAgreement;
    },
};

export const NameValidator: Validator = {
    isValid: (value: string): boolean => {
        return REGEX_NAME.test(value);
    },
    getErrorMessage: () => getMessage('JsValidationCheckName'),
}